import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Trans, t } from '@lingui/macro';
import { FileText, Calendar, Tag, X, Check, Settings2, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { My } from '../../services/My';
import { Fire } from '../../services/FireClient';
import { AiMemoDoc } from '../../services/FireClientAiMemo';
import { MemoCategory, getDefaultCategories } from '../../models/AiMemo';
import { cn } from '../../lib/utils';
import { pickCategoryEmoji } from '../../services/AiMemoAnalyzer';
import { IconPlus, IconSparkles } from '../components/FigmaIcons';

interface AiMemoListProps {
    roomId: string;
    onNewMemo: () => void;
    onOpenMemo: (id: string) => void;
}

const AiMemoList: React.FC<AiMemoListProps> = ({ roomId, onNewMemo, onOpenMemo }) => {
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [memos, setMemos] = useState<AiMemoDoc[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [customCategories, setCustomCategories] = useState<MemoCategory[]>([]);
    const [isManaging, setIsManaging] = useState(false);
    const [newCatName, setNewCatName] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [showTagDropdown, setShowTagDropdown] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);

    // Removed hardcoded roomId

    // Real-time subscription
    useEffect(() => {
        if (!roomId) return;
        setIsLoading(true);
        const unsub = Fire.aiMemo.subscribeToMemos(roomId, (docs) => {
            setMemos(docs);
            setIsLoading(false);
        });
        return () => unsub();
    }, [roomId]);

    // Load custom categories
    useEffect(() => {
        if (!roomId) return;
        Fire.aiMemo.getCustomCategories(roomId).then(setCustomCategories);
    }, [roomId]);

    // 외부 클릭 시 드롭다운 닫기
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
                setShowTagDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const allCategories = useMemo(() => [
        ...getDefaultCategories(),
        ...customCategories,
    ], [customCategories]);

    const categories = useMemo(() => [
        { id: 'all', name: t`All`, icon: '📋', color: '#6b7280' },
        ...allCategories,
    ], [allCategories]);

    // 전체 태그 수집 (중복 제거, 빈도순)
    const allTags = useMemo(() => {
        const tagCount = new Map<string, number>();
        memos.forEach(m => {
            (m.tags || []).filter(t => t.isActive).forEach(tag => {
                const name = tag.name || '';
                if (name) tagCount.set(name, (tagCount.get(name) || 0) + 1);
            });
        });
        return [...tagCount.entries()]
            .sort((a, b) => b[1] - a[1])
            .map(([name, count]) => ({ name, count }));
    }, [memos]);

    // 검색어로 태그 필터링 (드롭다운용)
    const filteredTags = useMemo(() => {
        const q = searchQuery.toLowerCase().trim();
        if (!q) return allTags;
        return allTags.filter(t => t.name.toLowerCase().includes(q));
    }, [allTags, searchQuery]);

    const toggleTag = (tagName: string) => {
        setSelectedTags(prev =>
            prev.includes(tagName)
                ? prev.filter(t => t !== tagName)
                : [...prev, tagName]
        );
    };

    const removeTag = (tagName: string) => {
        setSelectedTags(prev => prev.filter(t => t !== tagName));
    };

    // Filter by category + tags AND + search
    const filteredMemos = useMemo(() => {
        let result = selectedCategory === 'all'
            ? memos
            : memos.filter(m => m.category === selectedCategory);

        // 태그 AND 필터
        if (selectedTags.length > 0) {
            result = result.filter(m => {
                const mTags = (m.tags || []).filter(t => t.isActive).map(t => t.name);
                return selectedTags.every(st => mTags.includes(st));
            });
        }

        // 검색어 필터
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(m =>
                m.title?.toLowerCase().includes(q) ||
                m.content?.toLowerCase().includes(q) ||
                m.summary?.toLowerCase().includes(q) ||
                m.tags?.some(t => t.name.toLowerCase().includes(q))
            );
        }

        return result;
    }, [memos, selectedCategory, selectedTags, searchQuery]);

    const formatDate = (timestamp: any) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const mins = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (mins < 1) return t`Just now`;
        if (mins < 60) return t`${mins} mins ago`;
        if (hours < 24) return t`${hours} hours ago`;
        if (days < 7) return t`${days} days ago`;
        return date.toLocaleDateString();
    };

    const getCategoryInfo = (catId: string) => {
        return allCategories.find(c => c.id === catId);
    };

    // Category management handlers
    const handleAddCategory = async () => {
        const name = newCatName.trim();
        if (!name) return;
        const newCat: MemoCategory = {
            id: `custom_${Date.now()}`,
            name,
            icon: '📌',
            color: '#6b7280',
        };
        const updated = [...customCategories, newCat];
        setCustomCategories(updated);
        setNewCatName('');
        await Fire.aiMemo.saveCustomCategories(roomId, updated);

        // AI가 이모지 선택 (백그라운드)
        pickCategoryEmoji(name).then(async (emoji) => {
            const withEmoji = updated.map(c => c.id === newCat.id ? { ...c, icon: emoji } : c);
            setCustomCategories(withEmoji);
            await Fire.aiMemo.saveCustomCategories(roomId, withEmoji);
        });
    };

    const handleDeleteCategory = async (catId: string) => {
        const updated = customCategories.filter(c => c.id !== catId);
        setCustomCategories(updated);
        if (selectedCategory === catId) setSelectedCategory('all');
        await Fire.aiMemo.saveCustomCategories(roomId, updated);
    };

    return (
        <div className="p-4 md:p-6 lg:p-8 w-full space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                    <IconSparkles className="w-5 h-5 text-muted-foreground" />
                    <Trans>Memo</Trans>
                </h2>
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsManaging(prev => !prev)}
                        className={cn("h-8 w-8", isManaging && "text-foreground")}
                        title={t`Manage Categories`}
                    >
                        <Settings2 className="w-4 h-4" />
                    </Button>
                    <Button onClick={onNewMemo}>
                        <IconPlus className="w-4 h-4 mr-2" />
                        <Trans>New Memo</Trans>
                    </Button>
                </div>
            </div>

            {/* Search + Tag Dropdown */}
            <div ref={searchRef} className="space-y-2">
                <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setShowTagDropdown(true);
                        }}
                        onFocus={() => setShowTagDropdown(true)}
                        placeholder={t`Search or filter by tags...`}
                        className="w-full pl-9 pr-8 py-2 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ai/40 focus:border-ai"
                    />
                    {(searchQuery || selectedTags.length > 0) && (
                        <button
                            onClick={() => { setSearchQuery(''); setSelectedTags([]); }}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-card-foreground"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}

                    {/* Tag Dropdown (inline chips) */}
                    {showTagDropdown && filteredTags.length > 0 && (
                        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-background border rounded-lg shadow-lg p-2 max-h-48 overflow-y-auto">
                            <div className="flex flex-wrap gap-1.5">
                                {filteredTags.map(({ name, count }) => {
                                    const isSelected = selectedTags.includes(name);
                                    return (
                                        <button
                                            key={name}
                                            onClick={() => toggleTag(name)}
                                            className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full transition-colors ${isSelected
                                                    ? 'bg-ai text-on-primary-text'
                                                    : 'bg-muted text-foreground hover:bg-ai/15 hover:text-ai'
                                                }`}
                                        >
                                            #{name}
                                            <span className={`text-meta-sm ${isSelected ? 'text-purple-200' : 'text-muted-foreground'}`}>{count}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* Selected Tags (chips) */}
                {selectedTags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {selectedTags.map(tag => (
                            <span
                                key={tag}
                                className="inline-flex items-center gap-1 px-2 py-0.5 text-meta-sm rounded-full bg-ai text-on-primary-text"
                            >
                                #{tag}
                                <button onClick={() => removeTag(tag)} className="hover:bg-ai/80 rounded-full p-0.5">
                                    <X className="w-2.5 h-2.5" />
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Category Filter Chips */}
            <div className="flex gap-2 overflow-x-auto pb-2 custom-scrollbar flex-wrap">
                {categories.map(cat => {
                    const isCustom = customCategories.some(c => c.id === cat.id);
                    return (
                        <button
                            key={cat.id}
                            onClick={() => !isManaging && setSelectedCategory(cat.id)}
                            className={cn(
                                "shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all border cursor-pointer flex items-center gap-1",
                                selectedCategory === cat.id && !isManaging
                                    ? "bg-primary text-on-primary-text border-primary shadow-sm"
                                    : "bg-background text-foreground border-border hover:bg-muted/50"
                            )}
                        >
                            {cat.icon} {cat.name}
                            {isManaging && isCustom && (
                                <span
                                    onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat.id); }}
                                    className="ml-1 w-4 h-4 rounded-full bg-destructive/15 text-destructive hover:bg-destructive/20 flex items-center justify-center"
                                >
                                    <X className="w-2.5 h-2.5" />
                                </span>
                            )}
                        </button>
                    );
                })}

                {/* Add category (visible in manage mode) */}
                {isManaging && (
                    <div className="flex items-center gap-1 shrink-0">
                        <Input
                            value={newCatName}
                            onChange={e => setNewCatName(e.target.value)}
                            placeholder={t`Category Name`}
                            variant="basic"
                            className="w-24 h-9 text-sm"
                            onKeyDown={e => {
                                if (e.key === 'Enter') handleAddCategory();
                                if (e.key === 'Escape') { setIsManaging(false); setNewCatName(''); }
                            }}
                        />
                        <button onClick={handleAddCategory} className="text-card-foreground hover:text-foreground p-1">
                            <Check className="w-4 h-4" />
                        </button>
                    </div>
                )}
            </div>

            {/* Memo List */}
            {isLoading ? (
                <div className="text-center py-16">
                    <div className="w-8 h-8 border-2 border-border border-t-transparent rounded-full animate-spin mx-auto" />
                    <p className="text-sm text-muted-foreground mt-3"><Trans>Loading memos...</Trans></p>
                </div>
            ) : filteredMemos.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-sm">
                        {selectedCategory === 'all'
                            ? t`No memos yet. Create your first memo!`
                            : t`No memos in this category.`}
                    </p>
                    <Button variant="outline" className="mt-4" onClick={onNewMemo}>
                        <IconPlus className="w-4 h-4 mr-1" />
                        <Trans>Create New Memo</Trans>
                    </Button>
                </div>
            ) : (
                <div className="space-y-3">
                    {filteredMemos.map(memo => {
                        const cat = getCategoryInfo(memo.category);
                        return (
                            <Card
                                key={memo.id}
                                className="p-4 cursor-pointer hover:shadow-md transition-all active:scale-[0.99]"
                                onClick={() => onOpenMemo(memo.id)}
                            >
                                <div className="flex items-start gap-3">
                                    {/* Category icon */}
                                    <div
                                        className="w-10 h-10 rounded-xl flex items-center justify-center text-lg shrink-0"
                                        style={{ backgroundColor: (cat?.color || '#6b7280') + '15' }}
                                    >
                                        {cat?.icon || '📝'}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-sm truncate">
                                            {memo.title || t`Untitled`}
                                        </h3>
                                        <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">
                                            {memo.summary || memo.content || ''}
                                        </p>

                                        {/* Tags */}
                                        {memo.tags?.filter(t => t.isActive).length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-1.5">
                                                {memo.tags?.filter(t => t.isActive).slice(0, 5).map((tag, i) => (
                                                    <span
                                                        key={i}
                                                        className="px-1.5 py-0 text-meta-sm rounded-full bg-ai-bg text-ai"
                                                    >
                                                        #{tag.name}
                                                    </span>
                                                ))}
                                                {(memo.tags?.filter(t => t.isActive).length || 0) > 5 && (
                                                    <span className="text-meta-sm text-muted-foreground">+{(memo.tags?.filter(t => t.isActive).length || 0) - 5}</span>
                                                )}
                                            </div>
                                        )}

                                        {/* Category + Date */}
                                        <div className="flex items-center gap-2 mt-2">
                                            {cat && (
                                                <span className="text-meta-sm font-medium text-card-foreground">
                                                    {cat.icon} {cat.name}
                                                </span>
                                            )}
                                            <span className="text-meta-sm text-muted-foreground flex items-center gap-1 ml-auto">
                                                <Calendar className="w-3 h-3" />
                                                {formatDate(memo.created)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default AiMemoList;
